// Client-side helpers for the Menus page. Validation mirrors the API rules
// (apps/api/src/menus.ts) so problems surface before the save round-trip.

export interface MenuItem {
  label: string;
  page?: string;
  url?: string;
}

const PAGE_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
const SAFE_URL_PATTERN = /^(https?:\/\/|mailto:|tel:|\/)/i;
const MAX_ITEMS = 30;
const MAX_LABEL_LENGTH = 80;
const MAX_URL_LENGTH = 512;

// UI copy per menu slot. Keyed by the slugs the API defines.
export const MENU_HINTS: Record<string, string> = {
  header: "The main navigation at the top of every page.",
  footer: "Links shown at the bottom of every page — privacy policy, terms, and the like."
};

/** A menu item is in "page mode" when it links to a site page (vs a custom URL). */
export function isPageItem(item: MenuItem): boolean {
  return item.page !== undefined;
}

/** Validate one item; returns a user-facing problem or "" when it's saveable. */
export function menuItemProblem(item: MenuItem): string {
  if (!item || typeof item.label !== "string" || !item.label.trim()) {
    return "Add a label.";
  }
  if (item.label.trim().length > MAX_LABEL_LENGTH) {
    return `Keep labels under ${MAX_LABEL_LENGTH} characters.`;
  }
  if (isPageItem(item)) {
    const page = item.page ?? "";
    if (!PAGE_SLUG_PATTERN.test(page) || page.length > 50) {
      return "Choose a page.";
    }
    return "";
  }
  const url = (item.url ?? "").trim();
  if (!url) {
    return "Enter a link URL.";
  }
  if (url.length > MAX_URL_LENGTH || !SAFE_URL_PATTERN.test(url)) {
    return "Links must start with https://, http://, mailto:, tel: or /.";
  }
  return "";
}

/** First problem across a whole menu, gating its Save button. */
export function menuProblem(items: MenuItem[]): string {
  if (items.length > MAX_ITEMS) {
    return `Menus are limited to ${MAX_ITEMS} items.`;
  }
  for (const item of items) {
    const problem = menuItemProblem(item);
    if (problem) return problem;
  }
  return "";
}

/** Reorder an item by delta positions; returns the input when out of range. */
export function moveItem<T>(items: T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (index < 0 || index >= items.length || target < 0 || target >= items.length) {
    return items;
  }
  const next = items.slice();
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved as T);
  return next;
}

/** Normalise items to the API payload shape (trimmed, one link field each). */
export function toSavePayload(items: MenuItem[]): MenuItem[] {
  return items.map((item) =>
    isPageItem(item)
      ? { label: item.label.trim(), page: item.page }
      : { label: item.label.trim(), url: (item.url ?? "").trim() }
  );
}
